
import React from 'react';
import { Card, Collapse, Button } from 'antd';
import { HiOutlineExternalLink } from 'react-icons/hi';

const { Panel } = Collapse;

const HELP_CENTER = 'https://support.priorityprospect.com/knowledgebase';

const FAQ_ITEMS = {
    own_domains: {
        question: 'Do I need to own my domains already?',
        answer: 'Yes. You need to own the domains you want to host. Priority Prospect does not sell or register domain names, so you must purchase them from a domain registrar first.'
    },

    included: {
        question: "What's included with each website?",
        answer: 'Each website comes with a dedicated US IP address, a hosting account, a free SSL certificate, and automatic WordPress installation and updates. WordPress is installed after your domain points to its assigned IP address.'
    },

    start_small: {
        question: 'Can I start with just one website?',
        answer: 'Yes! You can start with a single domain and a dedicated IP address from $2.89/month, then add more websites whenever you need them.'
    },

    choose_locations: {
        question: 'Can I choose different IP locations?',
        answer: 'Yes! Simple mode automatically assigns dedicated US IP addresses. For specific locations, switch to Advanced mode to choose IP addresses from our worldwide network.'
    },

    getting_started: {
        question: 'How do I get started with Priority Prospect?',
        answer: (
            <span>
                Follow our{' '}
                <a
                    href={`${HELP_CENTER}/article/getting-started-with-priority-prospect`}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    getting started guide
                </a>{' '}
                to learn how to set up your account.
            </span>
        )
    },

    first_ips: {
        question: 'How do I order IP addresses?',
        answer: "Click 'IP Addresses' in the sidebar, create or open an IP group, and click 'Add IP Addresses'. Select your preferred locations and quantities to complete your order."
    },

    ip_group: {
        question: 'What is an IP group?',
        answer: 'An IP group allows you to organize and manage your IP addresses. You can add more IP addresses to an existing group and use both shared and dedicated IPs within the same group.'
    },

    ip_types: {
        question: "What's the difference between shared and dedicated IP addresses?",
        answer: 'A dedicated IP address is assigned exclusively to your account, while a shared IP address is used by multiple customers.'
    },

    find_ips: {
        question: 'Where can I find my IP addresses?',
        answer: "Click 'IP Addresses' in the sidebar and open your IP group to view its IP addresses. If you have an active order but cannot find the expected IP addresses, please contact our support team."
    },

    more_ips: {
        question: 'How do I order more IP addresses?',
        answer: "Click 'IP Addresses' in the sidebar, open your IP group, and click 'Add IP Addresses'. Select the locations and quantities you need, then complete your order."
    },

    locations: {
        question: 'How do I order IP addresses from different locations?',
        answer: "Click 'IP Addresses' in the sidebar, open your IP group, and click 'Add IP Addresses'. Choose from 46 geographic locations and 86+ data centers worldwide, select how many IP addresses you want from each location, and complete your order. Availability and pricing vary by location."
    },

    mix_ips: {
        question: 'Can I mix shared and dedicated IP addresses?',
        answer: 'Yes! You can use both shared and dedicated IP addresses within the same IP group.'
    },

    add_domain: {
        question: 'How do I add a domain to my account?',
        answer: "Click 'Domains' in the sidebar, open your domain group, and select 'Add Domains'. Enter the domain names you want to add and assign them to your available IP addresses."
    },

    assign_domain: {
        question: 'How do I assign or change the IP address of a domain?',
        answer: "You can select an IP address when adding a new domain. To change the IP address of an existing domain, open the domain's overview and click 'Modify IP Address' to select a different IP address."
    },

    domain_dns: {
        question: 'How do I point my domain to its IP address?',
        answer: "You can update your domain's A record at your DNS provider to point to its assigned IP address. Alternatively, you can use private nameservers by registering them and assigning them to your domain at your domain registrar. DNS changes may take some time to propagate."
    },

    more_domains: {
        question: 'How do I add more domains?',
        answer: "Click 'Domains' in the sidebar, open your domain group, and select 'Add Domains'. You can then add new domains and assign them to your available IP addresses."
    },

    migrate: {
        question: 'Can I migrate an existing website automatically?',
        answer: (
            <span>
                Yes! You can automatically migrate an existing website to your hosting account.{' '}
                <a
                    href={`${HELP_CENTER}/article/can-i-migrate-my-website-automatically-to-my-hosting-account`}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    Learn how to migrate your website
                </a>.
            </span>
        )
    },

    custom_server: {
        question: 'Can I use my own web server?',
        answer: "Absolutely! You can use your own web server with Priority Prospect IP addresses. Just add a custom server from the 'Hosting' menu."
    },

    website_slots: {
        question: 'Can I host multiple websites on one IP address?',
        answer: 'Yes! Dedicated IP addresses support additional website slots, allowing you to host multiple websites on the same IP address. You can add extra website slots to your existing dedicated IP addresses.'
    },
};

const FAQ_SETS = {
    new: {
        SIMPLE: [
            'own_domains',
            'included',
            'choose_locations',
            'start_small'
        ],
        ADVANCED: [
            'getting_started',
            'first_ips',
            'ip_group',
            'ip_types',
            'own_domains'
        ]
    },

    no_ips: {
        SIMPLE: [
            'find_ips',
            'getting_started',
            'first_ips',
            'choose_locations'
        ],
        ADVANCED: [
            'find_ips',
            'first_ips',
            'ip_group',
            'locations'
        ]
    },

    no_domains: {
        SIMPLE: [
            'add_domain',
            'assign_domain',
            'domain_dns',
            'migrate',
            'more_ips'
        ],
        ADVANCED: [
            'add_domain',
            'assign_domain',
            'domain_dns',
            'mix_ips',
            'migrate'
        ]
    },

    has_domains: {
        SIMPLE: [
            'more_domains',
            'domain_dns',
            'migrate',
            'locations',
            'custom_server'
        ],
        ADVANCED: [
            'more_ips',
            'locations',
            'more_domains',
            'mix_ips',
            'custom_server',
            'website_slots'
        ]
    }
};

function getFAQ({
                    has_active_orders = false,
                    ip_address_count = 0,
                    domain_count = 0,
                    panel_mode = 'SIMPLE'
                }) {
    const ipCount = Number(ip_address_count) || 0;
    const domainCount = Number(domain_count) || 0;

    const mode = panel_mode === 'ADVANCED'
        ? 'ADVANCED'
        : 'SIMPLE';

    let stage;

    if (domainCount > 0) {
        stage = 'has_domains';
    } else if (ipCount > 0) {
        stage = 'no_domains';
    } else if (has_active_orders) {
        stage = 'no_ips';
    } else {
        stage = 'new';
    }

    return FAQ_SETS[stage][mode].map(id => ({
        id,
        ...FAQ_ITEMS[id]
    }));
}

function QuickFAQ({
                      has_active_orders,
                      ip_address_count,
                      domain_count,
                      panel_mode
                  }) {
    const faq = getFAQ({
        has_active_orders,
        ip_address_count,
        domain_count,
        panel_mode
    });

    return (
        <Card
            title="Quick FAQ"
            size="small"
            actions={[
                <a
                    key="help-center"
                    href={HELP_CENTER}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    <Button
                        type="text"
                        icon={
                            <HiOutlineExternalLink
                                style={{
                                    marginBottom: '-3px',
                                    marginRight: '5px'
                                }}
                            />
                        }
                    >
                        Search Help Center
                    </Button>
                </a>
            ]}
        >
            <Collapse ghost>
                {faq.map(item => (
                    <Panel header={item.question} key={item.id}>
                        <p>{item.answer}</p>
                    </Panel>
                ))}
            </Collapse>
        </Card>
    );
}

export default QuickFAQ;
